import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MessageSquare, Phone, Mail, Award, MapPin } from "lucide-react";
import { Reveal } from "@/lib/motion";

const REGIONS = ["", "Central", "Eastern", "Western", "Northern"];

export default function Agents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/agents", { params: region ? { region } : {} });
        if (!cancelled) setAgents(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [region]);

  return (
    <main data-testid="agents-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="eyebrow mb-3">Agent Network</div>
            <h1 className="font-display text-5xl md:text-7xl tracking-[-0.04em] leading-[0.95]">People, not portals.</h1>
            <p className="text-muted-foreground mt-5 max-w-lg text-lg leading-relaxed">
              Every TIGAWA agent is vetted, licensed, and locally rooted. Meet the humans behind every transaction.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <Select value={region} onValueChange={(v) => setRegion(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-full border-border w-56" data-testid="agents-filter"><SelectValue placeholder="Filter by region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {REGIONS.filter(Boolean).map((r) => <SelectItem key={r} value={r}>{r} Uganda</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => <div key={i} className="aspect-[3/4] bg-muted rounded-2xl animate-pulse" />)}
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-24 bg-muted/40 rounded-2xl">
            <h3 className="font-display text-2xl">No agents in that region yet.</h3>
            <Button onClick={() => setRegion("")} className="mt-5 rounded-full bg-primary hover:bg-primary/90">Show all</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="agents-grid">
            {agents.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.05}>
                <div className="group bg-card border border-border rounded-3xl overflow-hidden hover-lift" data-testid={`agent-${a.id}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                    <img src={a.photo || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80"} alt={a.name} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" loading="lazy" />
                    {a.featured && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground flex items-center gap-1">
                        <Award className="w-3 h-3" /> Top agent
                      </span>
                    )}
                    <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
                      {a.languages?.slice(0,3).map((l) => (
                        <span key={l} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background/90 backdrop-blur">{l}</span>
                      ))}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-primary">{a.role}</div>
                    <h3 className="font-display text-2xl font-semibold mt-1">{a.name}</h3>
                    <div className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {a.region} · {a.districts?.slice(0,2).join(", ")}</div>
                    <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">{a.bio}</p>
                    <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                      <div>
                        <div className="font-display text-2xl font-bold text-primary">{a.deals_closed}</div>
                        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Deals closed</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl font-bold">{a.years_experience}y</div>
                        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Experience</div>
                      </div>
                    </div>
                    <div className="mt-5 flex gap-2">
                      {a.whatsapp && (
                        <Button asChild size="sm" className="flex-1 rounded-full bg-primary hover:bg-primary/90" data-testid={`agent-wa-${a.id}`}>
                          <a href={`https://wa.me/${a.whatsapp.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer"><MessageSquare className="w-3.5 h-3.5" /> WhatsApp</a>
                        </Button>
                      )}
                      {a.phone && (
                        <Button asChild size="sm" variant="outline" className="rounded-full border-border" data-testid={`agent-call-${a.id}`}>
                          <a href={`tel:${a.phone}`}><Phone className="w-3.5 h-3.5" /></a>
                        </Button>
                      )}
                      {a.email && (
                        <Button asChild size="sm" variant="outline" className="rounded-full border-border" data-testid={`agent-email-${a.id}`}>
                          <a href={`mailto:${a.email}`}><Mail className="w-3.5 h-3.5" /></a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="bg-secondary text-secondary-foreground rounded-3xl p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow text-accent mb-3">Join TIGAWA</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">Are you an agent? Come earn with the best network in Uganda.</h2>
          </div>
          <div className="md:justify-self-end">
            <Button onClick={() => navigate("/contact")} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-7" data-testid="agents-join-btn">
              Apply to become an agent
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
